/**
 * Sport clubs dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SportClubsDataTable } from '@/components/sport-clubs/SportClubsDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateSportClubView } from '@/components/sport-clubs/CreateSportClubView';
import { EditSportClubView } from '@/components/sport-clubs/EditSportClubView';
import {
  sportClubsKeys,
  useSportClubPageActions,
} from '@/components/sport-clubs/sport-club-table-utils';
import { useDetailsView } from '@/hooks/useDetailsView';
import type { SportClub } from '@/types/sport-club.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Dashboard sport-clubs widget: optional layout width from the IE config. */
interface SportClubsSectionProps {
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: sport clubs list. */
export const SportClubsSection: React.FC<SportClubsSectionProps> = ({ layoutWidth = 'full' }) => {
  const clubActions = useSportClubPageActions();
  const { openRowId, openRow, closeRow } = useDetailsView();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editingClubId, setEditingClubId] = React.useState<string | null>(null);

  const handleView = (club: SportClub) => {
    openRow(club.id);
  };

  const handleEdit = (club: SportClub) => {
    setEditingClubId(club.id);
    closeRow();
  };

  const handleOpenCreate = () => {
    setCreateOpen(true);
    closeRow();
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
  };

  const handleCloseEdit = () => {
    setEditingClubId(null);
  };

  const handleSuccess = () => {
    setCreateOpen(false);
    setEditingClubId(null);
    void queryClient.invalidateQueries({ queryKey: sportClubsKeys.all });
  };

  return (
    <>
      <SportClubsDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        pageActions={{
          canView: clubActions.canViewClub,
          canEdit: clubActions.canEditClub,
          canDelete: clubActions.canDeleteClub,
          canExportExcel: clubActions.canExportClubs,
        }}
        onView={handleView}
        onEdit={handleEdit}
        isSection={true}
        layoutWidth={layoutWidth}
        showCreateButton={clubActions.canCreateClub}
        onCreate={handleOpenCreate}
      />

      <SlidePanel isOpen={createOpen} onClose={handleCloseCreate}>
        <CreateSportClubView onClose={handleCloseCreate} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editingClubId !== null} onClose={handleCloseEdit}>
        {editingClubId ? (
          <EditSportClubView
            key={editingClubId}
            clubId={editingClubId}
            onClose={handleCloseEdit}
            onSuccess={handleSuccess}
          />
        ) : null}
      </SlidePanel>
    </>
  );
};
